import { useEffect, useState } from "react";
import apiService from "../../../../../../../../service/Api";

//types
export interface BackendService {
  id: number;
  name: string;
  description: string;
  price: string;
}

export interface BackendPackageItem {
  id: number;
  name: string;
  price: string;
  description: string;
  package: number;
  services: BackendService[];
}

export interface BackendPackage {
  id: number;
  name: string;
  package_type: string;
  base_price: string;
  description: string;
  items: BackendPackageItem[];
}

export interface MappedPackage {
  id: number;
  name: string;
  price: number;
  package_type: string;
  description: string;
  features: string[];
  raw: BackendPackage;
}

interface PaginatedPackageResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: BackendPackage[];
}

interface UsePackagesReturn {
  packages: MappedPackage[];
  loading: boolean;
  error: string | null;
}

//hook
export const usePackages = (packageType?: string): UsePackagesReturn => {
  const [packages, setPackages] = useState<MappedPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPackages = async () => {
      setLoading(true);
      try {
        const endpoint = packageType
          ? `/api/packages/package/?package_type=${encodeURIComponent(packageType)}`
          : "/api/packages/package/";

        const response =
          await apiService.get<PaginatedPackageResponse>(endpoint);

        const mapped: MappedPackage[] = response.data.results.map((pkg) => ({
          id: pkg.id,
          name: pkg.name,
          price: parseFloat(pkg.base_price),
          package_type: pkg.package_type,
          description: pkg.description,
          // Flatten all service names from all items as feature bullets
          features: pkg.description
            ? pkg.description
                .split(/<\/p>|<br\s*\/?>/)
                .map((line) =>
                  line
                    .replace(/<[^>]*>/g, "")
                    .replace(/&nbsp;/g, " ")
                    .trim(),
                )
                .filter(Boolean)
            : [],
          raw: pkg,
        }));

        setPackages(mapped);
        setError(null);
      } catch (err: unknown) {
        const errorResponse = (
          err as { response?: { data?: { detail?: string } } }
        )?.response;
        setError(errorResponse?.data?.detail || "Failed to load packages.");
      } finally {
        setLoading(false);
      }
    };

    fetchPackages();
  }, [packageType]);

  return { packages, loading, error };
};
